/**
 * Strategist controls-only live surface (iPad / operator).
 * Route: /live/:teamId/sessions/:sessionId/matches/:matchId/controls
 * Design: docs/prototypes/stratos-live-strategist-controls.html
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIsStrategist } from '../hooks/useIsStrategist';
import { useLiveMatch } from '../hooks/useLiveMatch';
import { useNow } from '../hooks/useNow';
import { useStrategistActions } from '../hooks/useStrategistActions';
import { listMapStrategies } from '../liveSessionApi';
import { formatBuyType, formatMapName, teamShortName } from '../lib/formatters';
import { buildLivePlayerPath } from '../lib/pickJoinTarget';
import { formatClock, getPhaseRemainingSecs } from '../lib/roundClock';
import type { StrategySummary } from '../types';
import styles from './LiveControlsPage.module.css';

export default function LiveControlsPage() {
  const { teamId, sessionId, matchId } = useParams<{
    teamId: string;
    sessionId: string;
    matchId: string;
  }>();
  const navigate = useNavigate();
  const isStrategist = useIsStrategist(teamId);
  const { liveState, loading, error, refetch } = useLiveMatch({ teamId, sessionId, matchId });
  const now = useNow(500);
  const strategist = useStrategistActions({
    teamId: teamId ?? '',
    sessionId: sessionId ?? '',
    matchId: matchId ?? '',
    liveState,
    onSuccess: refetch,
  });

  const [strategies, setStrategies] = useState<StrategySummary[]>([]);
  const [strategiesLoading, setStrategiesLoading] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);

  const fullPath =
    teamId && sessionId && matchId
      ? buildLivePlayerPath({ teamId, sessionId, matchId, map: liveState?.map ?? '' })
      : '/live';

  useEffect(() => {
    const side = (liveState?.currentSide ?? '').trim().toUpperCase();
    const sideReady = side === 'T' || side === 'CT';
    if (!teamId || !liveState?.map || !isStrategist || !sideReady) {
      setStrategies([]);
      setStrategiesLoading(false);
      return;
    }
    let cancelled = false;
    setStrategiesLoading(true);
    void (async () => {
      try {
        const rows = await listMapStrategies(teamId, liveState.map, side);
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setStrategies(list.filter((s) => (s.status ?? '').toLowerCase() === 'active'));
      } catch {
        if (!cancelled) setStrategies([]);
      } finally {
        if (!cancelled) setStrategiesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, liveState?.map, liveState?.currentSide, isStrategist]);

  if (!teamId || !sessionId || !matchId) {
    return (
      <Gate
        title="Invalid link"
        body="This controls URL is missing team, session, or match id."
        actions={<Link className={styles.ghostBtn} to="/live">Back to Live</Link>}
      />
    );
  }

  if (loading && !liveState) {
    return <Gate title="Joining session…" body="Connecting to the match." />;
  }

  if (error && !liveState) {
    return (
      <Gate
        title="Could not load live session"
        body={error.message || 'Something went wrong.'}
        actions={
          <>
            <button type="button" className={styles.ghostBtn} onClick={refetch}>
              Retry
            </button>
            <Link className={styles.ghostBtn} to="/live">
              Lobby
            </Link>
          </>
        }
      />
    );
  }

  if (!isStrategist) {
    return (
      <Gate
        title="Strategist only"
        body="Live controls are available to team strategists."
        actions={
          <button type="button" className={styles.ghostBtn} onClick={() => navigate(fullPath)}>
            Full view
          </button>
        }
      />
    );
  }

  if (!liveState) {
    return (
      <Gate
        title="No live data"
        body="Live state is not available yet."
        actions={
          <button type="button" className={styles.ghostBtn} onClick={refetch}>
            Retry
          </button>
        }
      />
    );
  }

  const clockSecs = getPhaseRemainingSecs(liveState.currentRound, now);
  const clockLabel = clockSecs == null ? '—' : formatClock(clockSecs);
  const status = (liveState.currentRound?.status ?? '').toLowerCase();
  const busy = strategist.pendingAction != null;
  const canStart = status === 'pending' || status === 'buy';
  const canEnd = status === 'buy' || status === 'active';
  const canClock = status === 'buy' || status === 'active';
  const canNextRound = status !== 'buy' && status !== 'active';
  const planted = Boolean(liveState.currentRound?.bombPlantedAt) && !liveState.currentRound?.bombDefusedAt;
  const defused = Boolean(liveState.currentRound?.bombDefusedAt);
  const canPlant = status === 'active' && !liveState.currentRound?.bombPlantedAt;
  const canDefuse = status === 'active' && planted;

  const chipPending = status === 'pending' || status === 'buy' || status === '';
  const chipActive = status === 'active';
  const chipDone = status === 'completed';

  const currentSide = (liveState.currentSide ?? '').toUpperCase();
  const startingSide = (liveState.startingSide ?? '').toUpperCase();
  const mapLabel = formatMapName(liveState.map) || liveState.map;
  const teamShort = teamShortName('Team');

  const nextMeta =
    liveState.currentRound != null
      ? `For R${strategist.pendingRoundNumber ?? liveState.currentRound.roundNumber + 1}`
      : 'Next round';

  return (
    <div className={styles.shell}>
      <header className={styles.top}>
        <div className={styles.topLeft}>
          <div className={styles.brand}>
            Stratos <span>· Live</span>
          </div>
          <div className={styles.ctx}>
            <span className={styles.score}>
              {teamShort} {liveState.score.wins} – {liveState.score.losses}
            </span>
            {liveState.currentRound && (
              <>
                <span className={styles.dot} />
                <span>R{liveState.currentRound.roundNumber}</span>
              </>
            )}
            {mapLabel && (
              <>
                <span className={styles.dot} />
                <span>{mapLabel}</span>
              </>
            )}
            {currentSide && (
              <>
                <span className={styles.dot} />
                <span className={currentSide === 'CT' ? styles.sideCt : styles.sideT}>
                  {currentSide === 'CT' ? 'CT-Side' : currentSide === 'T' ? 'T-Side' : currentSide}
                </span>
              </>
            )}
          </div>
          <span className={styles.statusPill}>{status || 'standby'}</span>
        </div>
        <div className={styles.topRight}>
          <div className={styles.topClock}>{clockLabel}</div>
          <Link className={styles.deskLink} to={fullPath}>
            Full view
          </Link>
        </div>
      </header>

      <div className={styles.sideBar} style={{ display: 'flex', gap: 8, padding: '8px 16px', alignItems: 'center' }}>
        <span className={styles.meta}>Starting side</span>
        <button
          type="button"
          className={`${styles.btn} ${startingSide === 'T' ? styles.primary : ''}`}
          disabled={busy}
          onClick={() => void strategist.setStartingSide('T')}
        >
          Start T
        </button>
        <button
          type="button"
          className={`${styles.btn} ${startingSide === 'CT' ? styles.primary : ''}`}
          disabled={busy}
          onClick={() => void strategist.setStartingSide('CT')}
        >
          Start CT
        </button>
      </div>

      <div className={styles.body}>
        <section className={`${styles.col} ${styles.colLeft}`}>
          <div className={styles.sectionHead}>
            <h2>Next strategy</h2>
            <span className={styles.meta}>{nextMeta}</span>
          </div>
          {strategiesLoading && <p className={styles.empty}>Loading strategies…</p>}
          {!strategiesLoading && strategies.length === 0 && (
            <p className={styles.empty}>
              {(liveState.currentSide ?? '').toUpperCase() === 'T' ||
              (liveState.currentSide ?? '').toUpperCase() === 'CT'
                ? 'No strategies for this map and side'
                : 'Set starting side to load strategies'}
            </p>
          )}
          <div className={styles.stratList}>
            {strategies.map((s) => {
              const selected = s.id === strategist.selectedStrategyId;
              const sSide = (s.side || '').toUpperCase();
              return (
                <button
                  key={s.id}
                  type="button"
                  className={`${styles.stratCard} ${selected ? styles.stratSelected : ''}`}
                  disabled={busy}
                  onClick={() => strategist.selectStrategyDraft(s)}
                >
                  <div className={styles.stratRow}>
                    <div className={styles.stratName}>{s.name}</div>
                    <span className={styles.check} aria-hidden="true">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M5 12l5 5L20 7" />
                      </svg>
                    </span>
                  </div>
                  <div className={styles.tags}>
                    {sSide && (
                      <span className={`${styles.tag} ${sSide === 'CT' ? styles.tagCt : sSide === 'T' ? styles.tagT : ''}`}>
                        {sSide}
                      </span>
                    )}
                    {s.buyType && <span className={styles.tag}>{formatBuyType(s.buyType)}</span>}
                    {selected && strategist.strategyNeedsConfirm && (
                      <span className={`${styles.tag} ${styles.tagDraft}`}>Pending confirm</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {strategist.strategyNeedsConfirm && (
            <button
              type="button"
              className={`${styles.btn} ${styles.primary} ${styles.confirmStrategy}`}
              disabled={busy || !strategist.canConfirmStrategy}
              title={
                strategist.canConfirmStrategy
                  ? 'Send strategy to the next round'
                  : 'Finish the current round first'
              }
              onClick={() => void strategist.confirmStrategy()}
            >
              Confirm strategy
            </button>
          )}
        </section>

        <section className={`${styles.col} ${styles.colRight}`}>
          <div className={styles.stateRow} aria-label="Round status">
            <div className={`${styles.stateChip} ${chipPending ? styles.stateOn : ''}`}>Pending</div>
            <div className={`${styles.stateChip} ${chipActive ? styles.stateOn : ''}`}>Active</div>
            <div className={`${styles.stateChip} ${chipDone ? styles.stateOn : ''}`}>Done</div>
          </div>

          <div className={styles.block}>
            <div className={styles.blockLabel}>Round</div>
            <div className={styles.stack}>
              <button
                type="button"
                className={`${styles.btn} ${styles.primary}`}
                disabled={!canStart || busy}
                onClick={() => void strategist.start()}
              >
                Start round
              </button>
              <div className={styles.btnRow}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.win}`}
                  disabled={!canEnd || busy}
                  onClick={() => void strategist.endWin()}
                >
                  Win
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.loss}`}
                  disabled={!canEnd || busy}
                  onClick={() => void strategist.endLoss()}
                >
                  Loss
                </button>
              </div>
              <button
                type="button"
                className={styles.btn}
                disabled={!canClock || busy}
                onClick={() => void strategist.resetClock()}
              >
                Reset
              </button>
            </div>
            {strategist.actionError && <p className={styles.error}>{strategist.actionError}</p>}
          </div>

          <div className={styles.block}>
            <div className={styles.blockLabel}>Match</div>
            <div className={styles.stack}>
              <button
                type="button"
                className={styles.btn}
                disabled={!canNextRound || busy}
                title={
                  canNextRound
                    ? 'Create or open the next pending round'
                    : 'Finish the current round first'
                }
                onClick={() => void strategist.nextRound()}
              >
                Next round
              </button>
              {confirmEnd ? (
                <div className={styles.btnRow}>
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.danger}`}
                    disabled={busy}
                    onClick={() => {
                      setConfirmEnd(false);
                      void strategist.endMatch().then((result) => {
                        if (result) navigate('/live', { state: { endedMatch: result } });
                      });
                    }}
                  >
                    Confirm end?
                  </button>
                  <button
                    type="button"
                    className={styles.btn}
                    disabled={busy}
                    onClick={() => setConfirmEnd(false)}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.danger}`}
                  disabled={busy}
                  onClick={() => setConfirmEnd(true)}
                >
                  End game
                </button>
              )}
            </div>
          </div>

          <div className={styles.block}>
            <div className={styles.blockLabel}>Game clock</div>
            <div className={styles.clockBig}>{clockLabel}</div>
            <div className={styles.presets}>
              {[115, 60, 40, 10].map((secs) => (
                <button
                  key={secs}
                  type="button"
                  className={`${styles.btn} ${styles.preset}`}
                  disabled={!canClock || busy}
                  onClick={() => void strategist.setClock(secs)}
                >
                  {formatClock(secs)}
                </button>
              ))}
              <button
                type="button"
                className={`${styles.btn} ${styles.preset}`}
                disabled={!canClock || busy || clockSecs == null}
                onClick={() => clockSecs != null && void strategist.setClock(Math.max(1, clockSecs - 10))}
              >
                −10s
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.preset}`}
                disabled={!canClock || busy || clockSecs == null}
                onClick={() => clockSecs != null && void strategist.setClock(clockSecs + 10)}
              >
                +10s
              </button>
            </div>
          </div>

          <div className={styles.block}>
            <div className={styles.blockLabel}>Bomb</div>
            <div className={styles.bombDisplay}>
              {defused ? (
                <>
                  <div className={`${styles.bombVal} ${styles.bombDefused}`}>DEFUSED</div>
                  <div className={styles.bombHint}>Confirmed</div>
                </>
              ) : planted ? (
                <>
                  <div className={styles.bombVal}>{clockLabel}</div>
                  <div className={styles.bombHint}>Planted · confirm defuse</div>
                </>
              ) : (
                <>
                  <div className={`${styles.bombVal} ${styles.bombIdle}`}>—</div>
                  <div className={styles.bombHint}>No bomb</div>
                </>
              )}
            </div>
            <div className={styles.btnRow}>
              <button
                type="button"
                className={`${styles.btn} ${styles.plant} ${planted ? styles.plantActive : ''}`}
                disabled={!canPlant || busy}
                onClick={() => void strategist.plant()}
              >
                Plant
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.defuse}`}
                disabled={!canDefuse || busy}
                onClick={() => void strategist.defuse()}
              >
                Defuse
              </button>
            </div>
          </div>

          <div className={styles.selectedFooter}>
            Next strategy
            <strong>{strategist.selectedStrategyName ?? 'Picking strat…'}</strong>
          </div>
        </section>
      </div>
    </div>
  );
}

function Gate({
  title,
  body,
  actions,
}: {
  title: string;
  body: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className={styles.shell}>
      <div className={styles.center}>
        <div className={styles.centerCard}>
          <h1 className={styles.centerTitle}>{title}</h1>
          <p className={styles.centerBody}>{body}</p>
          {actions && <div className={styles.centerActions}>{actions}</div>}
        </div>
      </div>
    </div>
  );
}
